import React, { useEffect, useState } from 'react';
import { ActivityIndicator, FlatList, Image, ImageBackground, useWindowDimensions } from "react-native"
import { Button, Text, View } from '../Components/Themed';
import type { AthleteProps, CampaignParticipantProps, CampaignProps, CompanyProps, CompetitionProps, EventProps, LeagueProps, MarketProps, MatchProps, OrderAnalyticsProps, OrderProps, PollCampaignProps, PollOptionProps, PollProps, PublicPlayerProps, TeamProps, TournamentProps } from '../types';
import { CampaignApi, CampaignHelpers } from '../Campaign/api';
import { MarketComponentApi } from '../MarketComponents/api';
import { useColors } from '../constants/useColors';
import { type Moment } from 'moment-mini';
import QRCode from 'react-qr-code';
import TickerApp from '../Ticker';
import BetAlert from './components/BetAlert';
import ParticipantsList from './components/ParticipantsList';
import ParticipantCard from './components/ParticipantCard';
import CampaignAnalytics from './components/CampaignAnalytics';
import SocketManager from '../Socket';
import NewPlayerAlert from './components/NewPlayerAlert';
import ActionList from './components/ActionList';
import { AutoScrollingFlatList, Icons, Toggle } from '../Components';
import { PollApi, PollCampaignApi, PollOptionApi } from '../Poll/api';
import FlashHolder from '../FlashMarket/FlashHolder';
import GolferAlert from './components/GolferAlert';

type CampaignAppProps = {
    player_id?:string,
    campaign_id:string,
    distinct_id:string,
    mode?:'golf',
    color_mode?:'light'|'dark',
    onCreateOrder?:(order:OrderProps) => void,
    action_loading?:boolean,
    onSelectPollCampaign:(pc:PollCampaignProps) => void,
    onSelectCompetition:(comp:CompetitionProps) => void,
    insets?:{ bottom:number, top:number, left:number, right:number },
    onBetNow?:() => void,
    onViewProfile?:(player:PublicPlayerProps) => void,
    onH2HChallenge?:(player:PublicPlayerProps) => void,
    onCheckedIn?:(campaign_participant:CampaignParticipantProps) => void,
    onCheckedOut?:(campaign_participant:CampaignParticipantProps) => void,
    auto?:boolean
}

const HEADER_HEIGHT = 75
const sections = ['header', 'prize', 'participants', 'toggle', 'desktop_data', 'mobile_ticker', 'mobile_player', 'mobile_leaderboard', 'engagements']
//const golf_sections = ['header', 'closest_pin', 'sponsors']
const Campaign = ({ player_id, campaign_id, insets, color_mode, mode, distinct_id, onSelectCompetition, onSelectPollCampaign, onCreateOrder, onViewProfile, onCheckedIn, onCheckedOut, onBetNow }:CampaignAppProps) => {
    const Colors = useColors();
    const [ needs_reload, setNeedsReload ] = useState(false);
    const [ golf_leader_size, setGolfLeaderSize ] = useState({ height:0, width:0 })
    const [ new_player_alert, setNewPlayerAlert ] = useState<CampaignParticipantProps|undefined>(undefined);
    const [ new_bet_alert, setNewBetAlert ] = useState<OrderProps | undefined>(undefined);
    //const [ refresh, setRefresh ] = useState(0);
    const { width } = useWindowDimensions();
    const view_mode = width > 710 ? 'desktop' : 'mobile';
    const [ active_toggle, setActiveToggle ] = useState('odds');
    const time_options = CampaignHelpers.getTimeOptions();
    //const ad_location = width > 1200 ? 'right': 'bottom';
    const [ socket_participants, setSocketParticipants ] = useState<CampaignParticipantProps[]>([]);
    const [ selected_participant, setSelectedParticipant ] = useState<CampaignParticipantProps|undefined>(undefined);
    const [ new_event, setNewEvent ] = useState<EventProps|undefined>(undefined);
    const [ new_tournaments, setNewTournaments ] = useState<TournamentProps|undefined>(undefined);
    const [ analytics, setAnalytics ] = useState<OrderAnalyticsProps[]>([]);
    const [ my_details, setMyDetails ] = useState<{
        my_loading:boolean,
        my_participant?:CampaignParticipantProps,

    }>({
        my_loading: false
    });
    const { my_loading, my_participant } = my_details;
    const [ embed_state, setEmbedState ] = useState<{
        loading:boolean,
        campaign?:CampaignProps,
        participant_page:number,
        action_page:number,
        campaign_participants:CampaignParticipantProps[],
        teams:TeamProps[],
        sponsors:CompanyProps[],
        athletes:AthleteProps[],
        leagues:LeagueProps[],
        tournaments:TournamentProps[],
        matches:MatchProps[],
        markets:MarketProps[],
        poll_campaigns:PollCampaignProps[],
        flash_markets:PollProps[],
        flash_options:PollOptionProps[],
        competitions:CompetitionProps[],
        orders:OrderProps[],
        events:EventProps[],
        players:PublicPlayerProps[]
    }>({
        loading:false,
        campaign_participants:[],
        events:[],
        leagues:[],
        tournaments:[],
        poll_campaigns: [],
        flash_options: [],
        flash_markets:[],
        sponsors: [],
        competitions: [],
        matches:[],
        athletes:[],
        participant_page: 0,
        markets:[],
        action_page:0,
        orders:[],
        players:[],
        teams:[]
    });
    const  { loading, orders, events, tournaments, flash_options, sponsors, athletes, competitions, flash_markets, poll_campaigns, campaign, participant_page, action_page, leagues, campaign_participants, players, teams, } = embed_state;
    //const visible_orders = orders.sort((a,b) => moment(b.create_datetime).unix() - moment(a.create_datetime).unix()).slice((action_page-1) * 5, ((action_page-1) * 5) + 5)
    useEffect(() => {
        if(!campaign_id){ return }
        startUp(campaign_id);
    },[campaign_id]);

    useEffect(() => {
        if(!player_id || !campaign_id){ return }
        getMyData(campaign_id);
    },[campaign_id, player_id])

    const getMyData = async(id:string) => {
        const resp = await CampaignApi.getMyCampaignParticipants();
        //find this campaign
        const this_cp = resp.campaign_participants.find(cp => cp.campaign_id == id);
        setMyDetails({
            ...my_details,
            my_loading:false,
            my_participant: this_cp
        });
        if(this_cp?.status == 'active' && onCheckedIn){ onCheckedIn(this_cp) }

    }

    useEffect(() => {
        if(socket_participants.length == 0){ return }
        handleNewParticipants(socket_participants);
    }, [JSON.stringify(socket_participants)]);

    const handleNewParticipants = async(cps:CampaignParticipantProps[]) => {
        if(cps.length == 0){ return }
        let mine = cps.find(cp => cp.player_id == player_id);
        if(mine){
            setMyDetails({ ...my_details, my_participant:mine })
        }
        //Do we have this player
        let needed_player_ids:string[] = []
        let plsers:PublicPlayerProps[] = []
        cps.map(cp => {
            let found = players.find(p => p.player_id == cp.player_id);
            if(!found){ needed_player_ids.push(cp.player_id) }
            else { plsers.push(found) }
        });
        plsers = plsers.concat(await CampaignApi.getPlayersByPlayerIds(needed_player_ids));
        //Set new bettor!!
        let new_player = cps.filter(cp => cp.status == 'active')[0]
        if(mode != 'golf' && new_player && new_player.player_id != player_id){ setNewPlayerAlert(new_player) }

        if(mode == 'golf'){
            //Old player
            let exist = campaign_participants.find(cp => cp.campaign_participant_id == socket_participants[0]?.campaign_participant_id);
            if(exist?.payload?.status == 'active' && campaign_participants[0]?.payload.status == 'closed'){
                setNewPlayerAlert(new_player);
            }
        }

        //Ok! Now lets update the participants
        setEmbedState({
            ...embed_state,
            players: players.filter(p => !plsers.find(np => np.player_id == p.player_id)).concat(plsers),
            campaign_participants: campaign_participants.filter(cp => !cps.find(ncp => ncp.campaign_participant_id == cp.campaign_participant_id)).concat(cps)
        })
    }

    useEffect(() => {
        if(!new_event){ return }
        setEmbedState({
            ...embed_state,
            events: events.filter(e => e.event_id != new_event.event_id).concat(new_event)
        })
        setNewEvent(undefined);
    },[new_event]);

    useEffect(() => {
        if(!new_tournaments){ return }
        setEmbedState({
            ...embed_state,
            tournaments: tournaments.filter(e => e.tournament_id != new_tournaments.tournament_id).concat(new_tournaments)
        })
        setNewEvent(undefined);
    },[new_tournaments]);

    const startUp = async(campaign_id:string) => {
        CampaignApi.setEnvironment();
        MarketComponentApi.setEnvironment();
        getData(campaign_id, false)
    }

    const handleLoadAnalytics = async(option?:{ label:string, to:number, from:number }) => {
        if(!option){ return }
        const analytics = await CampaignApi.getCampaignAnalytics(campaign_id, option.to, option.from);
        setAnalytics(analytics);
    }

    const getData = async(campaign_id:string, check_bets?:boolean) => {
        setEmbedState({ ...embed_state, loading:true });
        const c_resp = await CampaignApi.getCampaignById(campaign_id);
        const pls = await CampaignApi.getPlayersByPlayerIds(c_resp.campaign_participants.map(p => p.player_id));
        let tm_ids = c_resp.campaign_participants.map(cp => cp.payload?.team_id)
        let aths_ids = c_resp.campaign_participants.map(cp => cp.payload?.athlete_id);
        const ords = await CampaignApi.getCampaignAction(campaign_id);
        let vis_ords = [ ...ords ]
        let ev_ids = ords.filter(o => o.event_type == 'team').map(o => o.event_id);
        let tn_ids = ords.filter(o => o.event_type == 'tournament').map(o => o.event_id);
        const action_pages = Math.ceil(vis_ords.length / 5);
        const participant_pages = Math.ceil(c_resp.campaign_participants.length / 5);
        let next_participant_page = participant_page + 1
        if(next_participant_page > participant_pages){ next_participant_page = 1 }
        let comps:CompetitionProps[] = []
        let pcs: PollCampaignProps[] = []
        let fls: PollProps[] = []
        let opts: PollOptionProps[] = []
        if(c_resp.campaign && c_resp.campaign.poll_campaign_ids){ 
            pcs = await CampaignApi.getPollCampaignsByIds(c_resp.campaign.poll_campaign_ids)
            let campaign_id = pcs[0]?.poll_campaign_id
            if(campaign_id){ 
                PollCampaignApi.setEnvironment();
                let flash_resp = await PollApi.getPollsByCampaignId(campaign_id); 
                fls = flash_resp.polls
                let poll_ids = [ ...new Set(fls.map(p => p.poll_id)) ]
                opts = await PollOptionApi.getPollOptionsByPollIds(poll_ids);
            }
        }
        if(c_resp.campaign && c_resp.campaign.competition_ids){ comps = await CampaignApi.getCompetitionsByIds(c_resp.campaign.competition_ids) }

        let next_action_page = action_page + 1
        if(next_action_page > action_pages){ next_action_page = 1 }
        handleLoadAnalytics(time_options[0]);
        if(check_bets){
            let new_bet = vis_ords.find(o => !orders.map(old_o => old_o.order_id.toString()).includes(o.order_id.toString()))
            if(new_bet){
                setNewBetAlert(new_bet);
                if(new_bet.event_type == 'team'){ ev_ids.push(new_bet.event_id) }
                if(new_bet.event_type == 'tournament'){ tn_ids.push(new_bet.event_id) }
                if(new_bet.side_type == 'team'){ tm_ids.push(new_bet.side_id) }
                if(new_bet.side_type == 'athlete'){ aths_ids.push(new_bet.side_id) }
            }
        }
        
        const evs = await MarketComponentApi.getEventsByEventIds(ords.filter(o => o.event_type == 'team').map(o => o.event_id));
        const tns = await MarketComponentApi.getTournamentsByTournamentIds(tn_ids);
        const tms = await MarketComponentApi.getTeamsByIds(tm_ids);
        const aths = await MarketComponentApi.getAthletesByIds(aths_ids);
        const lgs = await MarketComponentApi.getLeagues();
        const spnsrs = await CampaignApi.getCompaniesByIds(c_resp.campaign?.sponsors ?? [])
        setEmbedState({
            ...embed_state,
            players:pls, 
            campaign: c_resp.campaign,
            campaign_participants: c_resp.campaign_participants,
            teams: tms,
            sponsors: spnsrs,
            flash_markets: fls,
            flash_options: opts,
            leagues: lgs,
            competitions:comps,
            poll_campaigns: pcs,
            tournaments: tns,
            athletes: aths,
            participant_page: next_participant_page,
            action_page: next_action_page,
            events: evs,
            orders: vis_ords,
            loading:false
        });
        setTimeout(() => {
            getData(campaign_id)
        }, 240000); //Update every 4 minutes
    }

    const handleCheckIn = async() => {
        setMyDetails({ ...my_details, my_loading: true })
        const new_cp = await CampaignApi.joinCampaign(campaign_id);
        setMyDetails({ 
            ...my_details, 
            my_participant: new_cp,
            my_loading: false 
        });
        if(new_cp && onCheckedIn){ onCheckedIn(new_cp) }
    }

    const handleCheckOut = async() => {
        if(!my_participant){ return }
        setMyDetails({ ...my_details, my_loading: true })
        const new_cp = await CampaignApi.leaveCampaign(my_participant.campaign_participant_id);
        setMyDetails({ 
            ...my_details, 
            my_loading: false,
            my_participant: new_cp
        });
        if(new_cp && onCheckedOut){ onCheckedOut(new_cp) }
    }


    const renderTicker = () => {
        return (
            <TickerApp
                league_ids={campaign?.league_ids ?? []}
                distinct_id={distinct_id}
                onCreateOrder={onCreateOrder}
                color_mode={color_mode}
                onOrders={async(orders) => {
                    //find if the orders are new!
                    let new_bet = orders.find(o => o.campaign_id );
                    if(new_bet){ 
                        let og_bet = await MarketComponentApi.getOrderById(new_bet.order_id);
                        if(!og_bet){ return }
                        //Find the event
                        switch(og_bet.event_type){
                            case 'team':
                                let ev = events.find(e => e.event_id == og_bet.event_id);
                                if(!ev){
                                    let evs = await MarketComponentApi.getEventsByEventIds([new_bet.event_id]);
                                    setNewEvent(evs[0])
                                }
                                return setNewBetAlert(og_bet) 
                            case 'tournament':
                                let tn = tournaments.find(tn => tn.tournament_id == og_bet.event_id);
                                let ath = athletes.find(a => a.athlete_id == og_bet.side_id);
                                let tm = teams.find(t => t.team_id == og_bet.side_id);
                                if(!tn){
                                    let evs = await MarketComponentApi.getTournamentsByTournamentIds([new_bet.event_id]);
                                    setNewTournaments(evs[0])
                                }
                                if(og_bet.side_type == 'athlete' && !ath){
                                    let athls = await MarketComponentApi.getAthletesByIds([new_bet.side_id]);
                                    setEmbedState({ ...embed_state, athletes: athletes.concat(athls) })
                                }
                                if(og_bet.side_type == 'team' && !tm){
                                    let tms = await MarketComponentApi.getTeamsByIds([new_bet.side_id]);
                                    setEmbedState({ ...embed_state, teams: teams.concat(tms) })
                                }
                                return setNewBetAlert(og_bet);
                            default: return
                        }
                        
                    }
                }}
            />
        )
    }

    const renderGolfers = (data:{ item:CampaignParticipantProps, index:number }) => {
        const player = players.find(p => p.player_id == data.item.player_id);
        if(!player){ return <></> }
        return (
            <View type='row' style={{ padding:10, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                {data.item.payload.status == 'active' ?
                <Icons.GolferIcon size={16} color={Colors.text.h1} />
                : data.item.payload.status == 'pending' ?
                <Icons.InProgressIcon size={16} color={Colors.text.h1} />
                :
                <Icons.LockClosedIcon size={16} color={Colors.text.h1} />
                }
                <View style={{ flex:1, flexDirection:'row', marginLeft:15 }}>
                    <Image
                        source={{ uri: player.profile_pic && player.profile_pic != '' ? player.profile_pic : 'https://res.cloudinary.com/hoabts6mc/image/upload/v1722453927/default_man_n96ofq.webp' }}
                        style={{ height:35, width:35, borderRadius:100}}
                    />
                    <View style={{ flex:1, marginLeft:10 }}>
                        <Text theme='h1'>{player.first_name} {player.last_name}</Text>
                        <Text theme='description' style={{ marginTop:3 }}>@{player.username}</Text>
                    </View>
                </View>
                <Text theme='h1'>{data.item.payload.pin_distance == 999 ? 'MISS' : data.item.payload.pin_distance}</Text>
            </View>
        )
    }

    const renderEngagements = (data:{ item:{ id:string, type:string, scheduled_datetime:Moment }, index:number }) => {
        
        switch(data.item.type){
            case 'competition':
                let comp = competitions.find(c => c.competition_id == data.item.id);
                if(!comp){ return <></> }
                return (
                    <Button 
                        disabled={!onSelectCompetition}
                        style={{ flexDirection:'row', alignItems:'center', padding:10, borderBottomWidth:1, borderColor:Colors.borders.light }}
                        onPress={() => onSelectCompetition ? onSelectCompetition(comp) : console.log('')}
                    >
                        <Image
                            source={{ uri:comp.image?.url }}
                            style={{ height:40, width:40, borderRadius:4 }}
                            resizeMode='cover'
                        />
                        <View transparent style={{ flex:1, marginLeft:10 }}>
                            <Text theme='h1'>{comp.competition_name}</Text>
                            <Text theme='description' style={{ marginTop:3 }}>{comp.competition_description}</Text>
                        </View>
                        <Icons.ChevronIcon direction='right' size={12} color={Colors.text.action} />
                    </Button>
                )
            case 'poll_campaign':
                let pc = poll_campaigns.find(c => c.poll_campaign_id == data.item.id);
                if(!pc){ return <></> }
                return (
                    <Button 
                        disabled={!onSelectPollCampaign}
                        style={{ flexDirection:'row', alignItems:'center', padding:10, borderBottomWidth:1, borderColor:Colors.borders.light }}
                        onPress={() => onSelectPollCampaign ? onSelectPollCampaign(pc) : console.log('')}
                        >
                        <Image
                            source={{ uri:pc.campaign_image?.url }}
                            style={{ height:40, width:40, borderRadius:4 }}
                            resizeMode='cover'
                        />
                        <View transparent style={{ flex:1, marginLeft:10 }}>
                            <Text theme='h1'>{pc.name}</Text>
                            <Text theme='description' style={{ marginTop:3 }}>{pc.total_responders} Players</Text>
                        </View>
                        <Icons.ChevronIcon direction='right' size={12} color={Colors.text.action} />
                    </Button>
                )
            default: return <></>
        }
    }

    const renderUpcomingGolfers = (data: { item:PollProps, index:number }) => {
        const options = flash_options.filter(o => o.poll_id == data.item.poll_id);
        return (
            <View transparent style={{ flexDirection:'row', borderBottomWidth:1, borderColor:Colors.borders.light, borderBottomLeftRadius: 8 }}>
                <View type='header' style={{ padding:20, justifyContent:'center', alignItems:'center', borderRightWidth:1, borderColor:Colors.borders.light }}>
                    <Text theme='description' style={{ marginBottom:4 }}>Group</Text>
                    <Text theme='h1'>{data.item.priority}</Text>
                </View>
                {options.sort((a,b) => a.priority - b.priority).map(o => {
                    return (
                        <View transparent style={{ flex:1, justifyContent:'center', alignItems:'center', padding:10, borderRightWidth:1, borderColor:Colors.borders.light }}>
                            <Image 
                                source={{ uri: 'https://res.cloudinary.com/hoabts6mc/image/upload/v1722453927/default_man_n96ofq.webp' }}
                                style={{ height:40, width:40 }}
                                resizeMode='cover'
                            />
                            <Text theme='h2' style={{ marginTop:4 }} textAlign='center'>{o.option_name}</Text>
                        </View>
                    )
                })}
            </View>
        )
    }

    const renderClosedGolfers = (data: { item:PollProps, index:number }) => {
        const options = flash_options.filter(o => o.poll_id == data.item.poll_id);
        return (
            <View transparent style={{ flexDirection:'row', borderBottomWidth:1, borderColor:Colors.borders.light, borderBottomLeftRadius: 8 }}>
                <View type='header' style={{ padding:20, justifyContent:'center', alignItems:'center', borderRightWidth:1, borderColor:Colors.borders.light }}>
                    <Text theme='description' style={{ marginBottom:4 }}>Group</Text>
                    <Text theme='h1'>{data.item.priority}</Text>
                </View>
                {options.sort((a,b) => a.priority - b.priority).map(o => {
                    return (
                        <View transparent style={{ flex:1, justifyContent:'center', alignItems:'center', padding:10, borderWidth:1, borderColor:o.result_ind=='win' ? Colors.text.success : Colors.borders.light }}>
                            <Image 
                                source={{ uri: 'https://res.cloudinary.com/hoabts6mc/image/upload/v1722453927/default_man_n96ofq.webp' }}
                                style={{ height:50, width:50 }}
                                resizeMode='cover'
                            />
                            <Text theme='h2' style={{ marginTop:4 }} textAlign='center'>{o.option_name}</Text>
                            
                        </View>
                    )
                })}
            </View>
        )
    }

    const renderPastPoll = (data: { item:PollProps, index:number }) => {
        return (
            <View style={{ flexGrow:1, padding:10, flexDirection:'row', alignItems:'center' }}>
                <Text theme='h1'>{data.item.poll_question}</Text>
            </View>
        )
    }

    const renderSponsors = (data: { item:CompanyProps, index:number }) => {
        return (
            <View float style={{ flexDirection:'row', alignItems:'center', minWidth:300, maxWidth:350, margin:10, padding:10 }}>
                <Image
                    source={{ uri: data.item.company_image?.url }}
                    style={{ height:60, width:60, backgroundColor:Colors.text.white, borderRadius:8 }}
                    resizeMode='center'
                />
                <View transparent style={{ flex:1, marginLeft:10 }}>
                    <Text theme='h1'>{data.item.company_name}</Text>
                    <Text theme='description' style={{ marginTop:4 }}>{data.item.company_description}</Text>
                </View>
            </View>
        )
    }

    const renderSections = (data:{ item:string, index:number }) => {
        switch(data.item){
            case 'header':
                if(!campaign){ return <></> }
                return (
                    <View type='header' style={{ height:HEADER_HEIGHT, padding:10, paddingLeft:20, paddingRight:20, flexDirection:'row', alignItems:'center', borderBottomWidth:1, borderColor:Colors.borders.light }}>
                        <Image
                            source={{ uri: campaign.campaign_image?.url }}
                            style={{ height:55, width:55 }}
                            resizeMode='cover'
                        />
                        <View transparent style={{ flex:1, marginLeft:20 }}>
                            <Text theme='h1' size={18}>{campaign.campaign_name}</Text>
                            <Text theme='description' style={{ marginTop:4 }}>{campaign.description}</Text>
                        </View>
                        {view_mode == 'desktop' ?
                        <View transparent style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                            <View transparent style={{ marginRight:10 }}>
                                <Text theme='h1' textAlign='center'>CHECK IN</Text>
                                <Text theme='h1' textAlign='center'>HERE</Text>
                            </View>
                            <QRCode
                                value={`https://play.bettoredge.com/social/campaign/${campaign_id}`}
                                size={65}
                            />
                        </View>
                        :<></>}
                    </View>
                )
            case 'closest_pin':
                const active_poll = flash_markets[0]//.find(m => m.flash_active);
                const next_three = flash_markets.filter(m => m.priority > (active_poll?.priority??0)).sort((a,b) => a.priority - b.priority).slice(0,3);
                return (
                    <View style={{ flexDirection:'row', flexWrap:'wrap' }}>
                        <View nativeID='pin_leaderboard' style={{ flex:1, minWidth:400 }}>
                            <View>
                                <Image
                                    source={{ uri: 'https://res.cloudinary.com/hoabts6mc/image/upload/v1752434537/a95a5b63614b60844f9612cce6cc985a2ed2879c-1600x1067_bfgn61.jpg' }}
                                    style={{ height:400*0.6725, width:400 }}
                                    resizeMode='cover'
                                />
                            </View>
                        </View>
                        <View style={{ minWidth:400 }}>
                            <FlatList
                                data={campaign_participants.filter(cp => cp.status == 'active').sort((a,b) => parseFloat((a.payload.pin_distance ?? 9999)) - parseFloat((b.payload.pin_distance ?? 9999)))}
                                key={'closed_pin_leaders'}
                                keyExtractor={(item) => item.campaign_participant_id}
                                renderItem={renderGolfers}
                            />
                        </View>
                        <View nativeID='flash' style={{ flex:4, borderLeftWidth:1, borderColor:Colors.borders.light }}>
                            <View type='body' style={{ flex:1 }}>
                                <View style={{ flexDirection:'row', flex:1 }}>
                                    <View style={{ flex:1 }}>
                                         
                                            {active_poll ?
                                            <FlashHolder
                                                poll_id={active_poll?.poll_id}
                                                init_expanded
                                                hide_tabs
                                                onRequestAuthenticate={() => console.log('')}
                                                onRequestLocation={() => console.log('')}
                                                onFocusPosition={() => console.log('')}
                                            />
                                            :<></>}
                                    </View>
                                    <View style={{ flex:1, borderLeftWidth:1, borderColor:Colors.borders.light }}>
                                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                                            <View transparent style={{ flex:1 }}>
                                                <Text theme='h1'>Upcoming Groups</Text>
                                                <Text theme='description' style={{ marginTop:8 }}>Bet on who will get closest to the pin!</Text>
                                            </View>
                                        </View>
                                        <View>
                                            {next_three.map((p,i) => {
                                                return renderUpcomingGolfers({ item: p, index:i })
                                            })}
                                        </View>
                                    </View>
                                </View>
                            </View>
                        </View>
                    </View>
                )
            case 'last_ones':
                const past_three = flash_markets.filter(m => m.priority > (active_poll?.priority??0)).sort((a,b) => b.priority - a.priority).slice(0,5);
                return (
                    <View style={{ flex:1, borderLeftWidth:1, borderColor:Colors.borders.light }}>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                            <View transparent style={{ flex:1 }}>
                                <Text theme='h1'>Upcoming Groups</Text>
                                <Text theme='description' style={{ marginTop:8 }}>Bet on who will get closest to the pin!</Text>
                            </View>
                        </View>
                        {past_three.map((p,i) => {
                            return renderPastPoll({ item: p, index:i })
                        })}
                    </View>
                )
            case 'sponsors':
                return (
                    <View style={{ padding:10, borderTopWidth:1, borderColor:Colors.borders.light }}>
                        <View>
                            <Text theme='h1'>Sponsors</Text>
                            <Text theme='description' style={{ marginTop:4 }}>A massive thank you to all the amazing sponsors</Text>
                        </View>
                        <AutoScrollingFlatList
                            data={sponsors}
                            renderItem={renderSponsors}
                            horizontal
                            speed={50}
                        />
                    </View>
                )
            case 'prize':
                if(!campaign?.prize_description){ return <></> }
                return (
                    <View float style={{ margin:5, padding:10 }}>
                        <Text theme='h1' color={Colors.text.gold}>{campaign.prize_description}</Text>
                    </View>
                )
            case 'participants':
                return (
                    <View style={{ borderBottomWidth:3, borderColor:Colors.borders.light }}>
                        <ParticipantsList
                            campaign_participants={campaign_participants}
                            players={players}
                            horizontal
                            selected_participant={selected_participant?.campaign_participant_id}
                            onSelect={(cp) => setSelectedParticipant(cp)}
                            onDeselect={() => setSelectedParticipant(undefined)}
                        />
                    </View>
                )
            case 'mobile_ticker':
                if(view_mode != 'mobile'){ return <></> }
                if(active_toggle != 'odds'){ return <></> }
                if(selected_participant){ return <></> }
                if(!campaign){ return <></> }
                return (
                    <View>                        
                        {renderTicker()}
                    </View>
                )
            case 'toggle':
                if(view_mode != 'mobile'){ return <></> }
                if(selected_participant){ return <></> }
                return (
                    <View style={{ padding:10 }}>
                        <Toggle
                            selected_option={active_toggle}
                            onSelectOption={(toggle) => setActiveToggle(toggle)}
                            options={[
                                { key: 'odds', label: 'Live Odds' },
                                { key: 'leaderboard', label: 'Leaderboard' },
                                { key: 'engagements', label: 'Games' }
                            ]}
                        />
                    </View>
                )
            case 'mobile_leaderboard':
                if(view_mode != 'mobile'){ return <></> }
                if(active_toggle != 'leaderboard'){ return <></> }
                return (
                    <CampaignAnalytics
                        campaign_id={campaign_id}
                        players={players}
                        campaign_participants={campaign_participants}
                    />
                )
            case 'engagements':
                if(view_mode != 'mobile'){ return <></> }
                if(active_toggle != 'engagements'){ return <></> }
                const sorted_engagements = CampaignHelpers.sortEngagements(competitions, poll_campaigns);
                return (
                    <FlatList
                        data={sorted_engagements}
                        renderItem={renderEngagements}
                        keyExtractor={(item) => item.id.toString()}
                        key={'engagement_list_campaign'}
                    />
                )
            case 'mobile_player':
                if(view_mode != 'mobile'){ return <></> }
                let mobile_selected_player = players.find(p => p.player_id == selected_participant?.player_id);
                if(!selected_participant || !mobile_selected_player){ return <></> }
                return (
                    <View type='header'>
                        <ParticipantCard
                            campaign_participant={selected_participant}
                            player={mobile_selected_player}
                            analytics={analytics.filter(a => a.player_id == selected_participant.player_id)}
                            orders={orders.filter(o => o.player_id == selected_participant.player_id)}
                            events={events}
                            tournaments={tournaments}
                            matches={[]}
                        />
                    </View>
                )
            case 'desktop_data':
                if(view_mode != 'desktop'){ return <></> }
                if(!campaign){ return <></> }
                let selected_player = players.find(p => p.player_id == selected_participant?.player_id);
                return (
                    <View style={{ flexDirection:'row' }}>
                        <View nativeID='recent_action' style={{ width:350, borderRightWidth:3, borderColor:Colors.borders.light }}>
                            {selected_participant && selected_player ?
                            <View type='header'>
                                <ParticipantCard
                                    campaign_participant={selected_participant}
                                    player={selected_player}
                                    analytics={analytics.filter(a => a.player_id == selected_participant.player_id)}
                                    orders={orders.filter(o => o.player_id == selected_participant.player_id)}
                                    events={events}
                                    tournaments={tournaments}
                                    matches={[]}
                                />
                            </View>
                            :
                            <ActionList
                                orders={orders}
                                events={events}
                                tournaments={tournaments}
                                matches={[]}
                                players={players}
                            />
                            }
                        </View>
                        <View nativeID='ticker' style={{ flex:1, borderRightWidth:3, borderColor:Colors.borders.light }}>
                            {renderTicker()}
                        </View>
                        <View nativeID='leaderboard' style={{ width: 350, borderLeftWidth:1, borderColor:Colors.borders.light }}>
                            <CampaignAnalytics
                                campaign_id={campaign_id}
                                players={players}
                                campaign_participants={campaign_participants}
                            />
                        </View>
                    </View>
                )


            default: return <></>
        }
    }

    const new_player = players.find(p => p.player_id == new_player_alert?.player_id);
    const new_bet_player = players.find(p => p.player_id == new_bet_alert?.player_id);
    const new_bet_team = teams.find(t => t.team_id == new_bet_alert?.side_id);
    const new_bet_athlete = athletes.find(a => a.athlete_id == new_bet_alert?.side_id);
    const new_bet_event = events.find(e => e.event_id == new_bet_alert?.event_id);
    const new_bet_tournament = tournaments.find(t => t.tournament_id == new_bet_alert?.event_id);
    const new_bet_league = new_bet_event ? leagues.find(l => l.league_id == new_bet_event.league_id) : leagues.find(l => l.league_id == new_bet_tournament?.league_id);
    
    if(mode == 'golf'){
        const active_poll = flash_markets.filter(m => ['paused','active'].includes(m.status)).sort((a,b) => a.priority - b.priority)[0]
        const next_three = flash_markets.filter(m => m.priority > (active_poll?.priority??0)).sort((a,b) => a.priority - b.priority).slice(0,3);
        const past_three = flash_markets.filter(m => m.status == 'closed').sort((a,b) => b.priority - a.priority).slice(0,3)
        const active_participant = campaign_participants.find(cp => cp.payload.status == 'active');
        const active_golfer = players.find(p => p.player_id == active_participant?.player_id);
        return (
            <View style={{ flex:1 }}>
                <ImageBackground
                    style={{ flex:1 }}
                    source={{ uri: 'https://res.cloudinary.com/hoabts6mc/image/upload/v1752434537/a95a5b63614b60844f9612cce6cc985a2ed2879c-1600x1067_bfgn61.jpg' }}
                >
                        <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0 }} />
                        <View transparent style={{flex:1, flexDirection:'row' }}>
                            <View transparent style={{ flex:2, margin:10, minWidth:350 }} onLayout={(ev) => {
                                const { height, width } = ev.nativeEvent.layout;
                                setGolfLeaderSize({ height, width: width -2 });
                            }}>
                                <View float style={{ flex:1 }}>
                                    <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                                        <View transparent style={{ flex:1 }}>
                                            <Text theme='h1'>On the Tee Now!</Text>
                                            <Text theme='description'>Below are the current betting probabilities for the group on the tee!</Text>
                                        </View>
                                    </View>
                                    <View style={{ flex:1 }}>
                                        {active_poll ?
                                        <FlashHolder
                                            poll_id={active_poll?.poll_id}
                                            init_expanded
                                            hide_tabs
                                            hide_timer
                                            hide_footer
                                            no_highlight
                                            onRequestAuthenticate={() => console.log('')}
                                            onRequestLocation={() => console.log('')}
                                            onFocusPosition={() => console.log('')}
                                        />
                                        :<></>}
                                    </View>
                                    <View transparent>
                                        <Image
                                            source={{ uri: 'https://res.cloudinary.com/hoabts6mc/image/upload/v1752434537/a95a5b63614b60844f9612cce6cc985a2ed2879c-1600x1067_bfgn61.jpg' }}
                                            style={{ height:golf_leader_size.width*0.6725, width:golf_leader_size.width, alignSelf:'center', borderBottomRightRadius:8, borderBottomLeftRadius:8 }}
                                        />
                                        <View transparent style={{ position:'absolute', top:0, left:0, right:0, bottom:0, justifyContent:'center', alignItems:'center' }}>
                                            <View float style={{ padding:20 }}>
                                                <Text theme='h2' textAlign='center'>Windsong Farm South Course</Text>
                                                <Text theme='h1' style={{ marginTop:10 }} textAlign='center'>Hole 16</Text>
                                                <View transparent style={{ marginTop:10, padding:10, justifyContent:'center', alignItems:'center' }}>
                                                    <QRCode
                                                        value={`https://play.bettoredge.com/social/campaign/${campaign_id}`}
                                                        size={100}
                                                        style={{ backgroundColor:'transparent' }}
                                                    />
                                                </View>
                                            </View>
                                            
                                        </View>
                                    </View>
                                </View>
                            </View>
                            <View float style={{ flex:3, minWidth:350, margin:10 }}>
                                <View type='header' style={{ padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                                    <View transparent style={{ flex:1 }}>
                                        <Text theme='h1'>Closest to the hole #16</Text>
                                    </View>
                                </View>
                                {active_golfer ?
                                <View style={{ backgroundColor:Colors.views.gold, padding:10 }}>
                                    <Text theme='h2'>Now On The Tee</Text>
                                    <View transparent style={{ flexDirection:'row', marginTop:5}}>
                                        <Image
                                            source={{ uri: active_golfer.profile_pic && active_golfer.profile_pic != '' ? active_golfer.profile_pic : 'https://res.cloudinary.com/hoabts6mc/image/upload/v1722453927/default_man_n96ofq.webp' }}
                                            style={{ height:35, width:35, borderRadius:100}}
                                        />
                                        <View transparent style={{ flex:1, marginLeft:10 }}>
                                            <Text theme='h1' color={Colors.text.white}>{active_golfer.first_name} {active_golfer.last_name}</Text>
                                            <Text theme='description' style={{ marginTop:3 }}>@{active_golfer.username}</Text>
                                        </View>
                                        <Icons.GolferIcon size={28} color={Colors.text.white} />
                                    </View>
                                </View>
                                :<></>}
                                <View style={{ flex:1, borderBottomRightRadius:8, borderBottomLeftRadius:8 }}>
                                    <View style={{ flexDirection:'row' }}>
                                        <View transparent style={{ padding:10 }}>
                                            <Text theme='h1'>LEADERBOARD</Text>
                                            <Text theme='description' style={{ marginTop:4 }}>Closest to the pin - distance in feet from the pin</Text>
                                        </View>
                                        {loading ?
                                        <ActivityIndicator color={Colors.text.h1} size={14} />
                                        :<></>}
                                    </View>
                                   
                                    <FlatList
                                        data={campaign_participants.filter(cp => cp.status == 'active').sort((a,b) => parseFloat((a.payload.pin_distance ? a.payload.pin_distance : 9999)) - parseFloat((b.payload.pin_distance ? b.payload.pin_distance : 9999)))}
                                        key={'closed_pin_leaders'}
                                        keyExtractor={(item) => item.campaign_participant_id}
                                        renderItem={renderGolfers}
                                    />
                                </View>
                                <View type='header' style={{ padding:10, borderTopWidth:1, borderColor:Colors.borders.light, overflow:'hidden', borderBottomRightRadius:8, borderBottomLeftRadius:8 }}>
                                    <View transparent>
                                        <Text theme='h1'>Sponsors</Text>
                                        <Text theme='h2' style={{ marginTop:4 }}>A massive thank you to all the amazing sponsors</Text>
                                    </View>
                                    <AutoScrollingFlatList
                                        data={sponsors}
                                        renderItem={renderSponsors}
                                        horizontal
                                        speed={20}
                                    />
                                </View>
                            </View>
                            <View transparent style={{ flex:4 }}>
                                <View float nativeID='next_up' style={{ flexGrow:1, margin:10 }}>
                                    <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                                        <Icons.GolferIcon size={28} color={Colors.text.h1} />
                                        <View transparent style={{ flex:1, marginLeft:10 }}>
                                            <Text theme='h1'>Upcoming Groups</Text>
                                            <Text theme='description' style={{ marginTop:3 }}>Groups that are soon to be up!</Text>
                                        </View>
                                    </View>
                                    <View style={{ borderBottomRightRadius:8, borderBottomLeftRadius:8 }}>
                                        <FlatList
                                            data={next_three.sort((a,b) => a.priority - b.priority)}
                                            renderItem={renderUpcomingGolfers}
                                            key={'next_up'}
                                            keyExtractor={(item) => item.poll_id.toString()}
                                        />
                                    </View>
                                </View>
                                <View float nativeID='past' style={{ flexGrow:1, margin:10 }}>
                                    <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                                        <Icons.GolferIcon size={28} color={Colors.text.h1} />
                                        <View transparent style={{ flex:1, marginLeft:10 }}>
                                            <Text theme='h1'>Recently Completed Groups</Text>
                                            <Text theme='description' style={{ marginTop:3 }}>Winners from past groups!</Text>
                                        </View>
                                    </View>
                                    <View style={{ borderBottomRightRadius:8, borderBottomLeftRadius:8 }}>
                                        <FlatList
                                            data={past_three.sort((a,b) => b.priority - a.priority)}
                                            renderItem={renderClosedGolfers}
                                            key={'past_up'}
                                            keyExtractor={(item) => item.poll_id.toString()}
                                        />
                                    </View>
                                </View>
                                {campaign ?
                                    <View type='header' style={{ margin:10, flexDirection:'row', alignItems:'center', padding:10, borderRadius:8 }}>

                                        <Image
                                            source={{ uri: campaign.campaign_image?.url }}
                                            style={{ height:80, width:80, borderRadius:8 }}
                                            resizeMode='center'
                                        />
                                        <View transparent style={{ flex:1, marginLeft:20 }}>
                                            <Text theme='h1' size={18}>{campaign.campaign_name}</Text>
                                            <Text theme='description' style={{ marginTop:4 }}>{campaign.description}</Text>
                                        </View>
                                        
                                    </View>
                                :<></>}
                            </View>

                        </View>
                </ImageBackground>
                {new_player_alert && new_player ?
                <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0, alignItems:'center', paddingTop:100 }}>
                    <GolferAlert
                        campaign_participant={new_player_alert}
                        player={new_player}
                        view_mode={view_mode}
                        onViewProfile={onViewProfile}
                        onClose={() => setNewPlayerAlert(undefined)}
                    />
                </View>
                :<></>}
                <SocketManager
                    onConnect={() => {
                        if(!needs_reload){ return }
                    }}
                    onDisconnect={() => setNeedsReload(true)}
                    subscribed_events={['UPDATE_CAMPAIGN_PARTICIPANTS']}
                    onSocketEvent={(event) => {
                        switch(event.type){
                            case 'UPDATE_CAMPAIGN_PARTICIPANTS':
                                let this_participants = event.campaign_participants.filter((cp:CampaignParticipantProps) => cp.campaign_id == campaign_id);
                                if(this_participants.length == 0){ return }
                                return setSocketParticipants(event.campaign_participants);
                            default: return
                        }
                    }}
                />
            </View>
        )
    }
    
    return (
        <View style={{ flex:1 }}>
            <View style={{ flex:1 }}>
                <FlatList
                    data={sections}
                    renderItem={renderSections}
                    key={'campaign_sections'}
                    keyExtractor={item => item}
                />
            </View>
            {view_mode == 'mobile' ?
            <View type='footer' style={{ flexDirection:'row', padding:10, paddingBottom:(insets?.bottom ?? 0) + 10}}>
                {onBetNow ?
                <Button
                    type='action'
                    style={{ flex:1, justifyContent:'center', marginRight:4 }}
                    title={'Bet Now!'}
                    onPress={() => onBetNow()}
                />
                :<></>}
                {my_participant?.status == 'active' ?
                <Button
                    type='error'
                    style={{ flex:2, opacity:my_loading?0.5:1, alignItems:'center' }}
                    title={'Check Out'}
                    loading={my_loading}
                    disabled={my_loading}
                    onPress={() => handleCheckOut()}
                />
                :
                <Button
                    type='success'
                    style={{ flex:2, opacity: my_loading?0.5:1 }}
                    loading={my_loading}
                    disabled={my_loading}
                    title="Check In Now"
                    onPress={() => handleCheckIn()}
                />
                }
            </View>
            :<></>}
            {new_bet_alert ?
            <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0, alignItems:'center', paddingTop:100 }}>
                <BetAlert
                    order={new_bet_alert}
                    event={new_bet_event}
                    league={new_bet_league}
                    tournament={new_bet_tournament}
                    player={new_bet_player}
                    team={new_bet_team}
                    athlete={new_bet_athlete}
                    onClose={() => setNewBetAlert(undefined)}
                />
            </View>
            :<></>}
            {new_player_alert && new_player ?
            <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0, alignItems:'center', paddingTop:100 }}>
                <NewPlayerAlert
                    campaign_participant={new_player_alert}
                    player={new_player}
                    view_mode={view_mode}
                    onViewProfile={onViewProfile}
                    onClose={() => setNewPlayerAlert(undefined)}
                />
            </View>
            :<></>}
            <SocketManager
                onConnect={() => {
                    if(!needs_reload){ return }
                }}
                onDisconnect={() => setNeedsReload(true)}
                subscribed_events={['UPDATE_CAMPAIGN_PARTICIPANTS']}
                onSocketEvent={(event) => {
                    switch(event.type){
                        case 'UPDATE_CAMPAIGN_PARTICIPANTS':
                            let this_participants = event.campaign_participants.filter((cp:CampaignParticipantProps) => cp.campaign_id == campaign_id);
                            if(this_participants.length == 0){ return }
                            return setSocketParticipants(event.campaign_participants);
                        default: return
                    }
                }}
            />
        </View>
    )
}

export default Campaign
